import { useId, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cx } from '../../lib/cx';

import styles from './Toggle.module.css';

export interface ToggleProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'children'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** What is switched. Shown beside the switch, and its accessible name. */
  label: ReactNode;
  /** A line under the label that says what "on" means. */
  description?: ReactNode;
}

/** An on/off setting that takes effect at once, with its label. */
export function Toggle({ checked, onChange, label, description, className, disabled, ...rest }: ToggleProps) {
  const id = useId();
  return (
    <div className={cx(styles.row, className)}>
      <div className={styles.text}>
        <span id={`${id}-label`} className={styles.label}>
          {label}
        </span>
        {description ? (
          <span id={`${id}-description`} className={styles.description}>
            {description}
          </span>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={description ? `${id}-description` : undefined}
        disabled={disabled}
        className={styles.track}
        onClick={() => onChange(!checked)}
        {...rest}
      >
        <span className={styles.knob} />
      </button>
    </div>
  );
}
